"use client";

import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useScreenSize } from "../../utils/hooks/useScreenSize";
import { Icon } from "@trimble-creative-strategy/trimble-mxp-icons";
import { TerraLink } from "../../component/TerraLink/TerraLink";
import { CardEcommerce } from "../../component/CardEcommerce/CardEcommerce";
import { CommonDescription } from "../../component/CommonDescription/CommonDescription";
import { ScrollChevron } from "../../component/ScrollChevron/ScrollChevron";
import { renderCreditCardPaymentAssets } from "./LayoutPricingCardsStaticAssets";
import "./LayoutPricingCards.css";
import { layoutHeaderClasses, layoutSubheaderClasses, layoutEyebrowClasses } from "@/components/tailwindHelpers";
import { darkTextureWrapper, colorSettingDetection, renderHeaderConditionally } from "../../utils/functions";
interface tabsInterface {
  title: string;
  cards: card[];
}
interface MissingField {
  content: ContentContainer;
  missing: string[];
}
type ContentContainer = Record<string, any>;
type currencies = {
  displayName: string;
  machineName: string;
};
type details = {
  text: string;
};
type button = {
  label: string;
  destination: string;
};
type card = {
  cardType: string;
  header: string;
  subheader: string;
  currency: string;
  discount: string;
  discountCode: string;
  priceDescription: string;
  description: string;
  detailsHeader: string;
  details: details[];
  button: button;
  annual: boolean;
  monthly: boolean;
  notApplicable: boolean;
  priceList: any;
  indexAnimation: string;
  index: number;
  crossedOutPrice: string;
  id: string;
};
export interface LayoutPricingCards {
  eyebrow?: string;
  header?: string;
  description?: string | JSX.Element;
  subheader?: string;
  headerElement?: "h1" | "h2";
  cardType?: string;
  buttons?: TerraLink[];
  cards?: card[];
  presetTheme?: "t2-preset-theme-one" | "t2-preset-theme-two" | "t2-preset-theme-three" | "t2-preset-theme-four" | "t2-preset-theme-five" | "t2-preset-theme-six" | "t2-preset-theme-seven";
  isWrapped?: boolean;
  paymentText?: string;
  excludesTaxLabel?: string;
  comparePlansText?: string;
  toggleOptions?: string[];
  tabs?: tabsInterface[];
  currencies?: currencies[];
  displayCardPaymentOptionsMedia?: boolean;
  anchorTabQuery?: string;
  additionalPricingInformation?: string;
  viewMoreLabel?: string;
  viewLessLabel?: string;
}
export const LayoutPricingCards = ({
  eyebrow,
  header,
  headerElement = "h2",
  description,
  subheader,
  buttons,
  cardType,
  cards,
  presetTheme,
  isWrapped,
  paymentText,
  excludesTaxLabel,
  comparePlansText,
  currencies = [],
  toggleOptions,
  tabs,
  displayCardPaymentOptionsMedia,
  anchorTabQuery,
  additionalPricingInformation,
  viewMoreLabel,
  viewLessLabel
}: LayoutPricingCards) => {
  const [selected, setSelected] = useState<string>("annual");
  const [longDetailHeader, setLongDetailHeader] = useState(false);
  const [currencySelected, setCurrencySelected] = useState<currencies>({
    displayName: "$ USD",
    machineName: "USD"
  });
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [styling, setStyling] = useState({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const wrapperClasses = isWrapped ? "w-full px-5 lg:px-24 m-auto max-w-[1440px] t2-wrapper" : "";
  const colorSetting = colorSettingDetection(presetTheme || "");
  const isDark = colorSetting?.includes("dark") ?? false;
  const mainClasses = `py-[100px] overflow-x-hidden t2-lo-price-cards--t-${cardType} ${presetTheme || ""} ${colorSetting}`;
  const themeForCards = isDark ? "dark" : "normal";
  const finalHeader = renderHeaderConditionally(layoutHeaderClasses, header || "", headerElement);

  // validateContentIsPresent will return an object that contains an array of "missing" content (as missingFields: []) and an array of "valid" content as completeFields: [].
  // This will tell us the following:
  // 1. If all cards are missing the same content
  // 2. All cards have the same content
  // 3. If there is a mix of both (some cards have missing content while others have full content). We need this information so we can apply accurate heights for each section of the pricing card

  // If all cards are missing the same content, we need to shrink the height of the sections of the card to account for missing fields

  // If all cards are not missing content, we need the height of the sections of the card to be at maximum heights to account for present fields
  const validateContentIsPresent = (cards_0: ContentContainer[], specifiedFields: string) => {
    const missingFields: MissingField[] = [];
    const completeFields: ContentContainer[] = [];
    if (!cards_0?.length) {
      return {
        missingFields: [],
        completeFields: []
      };
    }
    if (!specifiedFields.length) {
      return {
        missingFields: [],
        completeFields: [...cards_0]
      };
    }
    cards_0.forEach(card => {
      const currentMissing: string[] = [];
      let isValid = true;
      if (!Object.prototype.hasOwnProperty.call(card, specifiedFields) || typeof card[specifiedFields] === "undefined" || card[specifiedFields] === null || card[specifiedFields] === "") {
        currentMissing.push(specifiedFields);
        isValid = false;
      }
      if (!isValid) {
        missingFields.push({
          content: card,
          missing: currentMissing
        });
      } else {
        completeFields.push(card);
      }
    });
    return {
      missingFields,
      completeFields
    };
  };
  const {
    width
  } = useScreenSize();
  const componentStyles = darkTextureWrapper(presetTheme || "");

  // Memoize `activeCards` and `activeCardsInTab` to prevent re-creation on every render.
  const {
    activeCards,
    activeCardsInTab
  } = useMemo(() => {
    const updateCurrencyPerCard = (currencySelected_0: currencies_0, card_0: card_0, index: number, currencies_0: currencies_0[]) => {
      const newCard = {
        ...card_0
      };
      newCard.indexAnimation = `${index}-annimation`;
      const priceEntry = newCard.priceList?.[currencySelected_0?.machineName];
      newCard.subheader = priceEntry?.currency || "";
      newCard.crossedOutPrice = priceEntry?.crossedOutPrice || "";
      if (priceEntry && "discount" in priceEntry) {
        newCard.discount = priceEntry.discount || "";
        newCard.discountCode = priceEntry.discountCode || "";
      }
      newCard.currency = currencies_0 && currencies_0.length && currencySelected_0?.machineName ? currencySelected_0.machineName : "";
      return newCard;
    };
    const cardsActive = cards?.length ? cards.filter(card_1 => card_1[selected as keyof typeof card_1] || card_1["notApplicable"]) : [];
    const updatedCards = cardsActive.map((card_2, index_0) => card_2.priceList ? updateCurrencyPerCard(currencySelected, card_2, index_0, currencies) : card_2);
    const slicedCards = updatedCards.slice(0, 3);
    let filteredTabs: tabsInterface[] = [];
    if (tabs) {
      filteredTabs = tabs.map(tab => {
        const filteredCards = tab.cards.filter(card_3 => card_3[selected as keyof typeof card_3] || card_3["notApplicable"]);
        const updatedTabCards = filteredCards.map((card_4, index_1) => card_4.priceList ? updateCurrencyPerCard(currencySelected, card_4, index_1, currencies) : card_4);
        return {
          title: tab.title,
          cards: updatedTabCards.slice(0, 3)
        };
      });
    }
    return {
      activeCards: slicedCards,
      activeCardsInTab: filteredTabs
    };
    /* eslint-disable react-hooks/use-memo, react-hooks/exhaustive-deps */
  }, [JSON.stringify(cards), selected, JSON.stringify(currencies), currencySelected, JSON.stringify(tabs)]);
  /* eslint-enable react-hooks/use-memo, react-hooks/exhaustive-deps */

  useEffect(() => {
    const getCharacterCountOfHeader = (cards_1: any) => {
      const getCount = cards_1?.map((card_5: any) => {
        if (card_5?.detailsHeader?.length) {
          return card_5?.detailsHeader?.length;
        }
        if (card_5?.title) {
          return card_5?.cards?.map((card_6: any) => {
            return card_6?.detailsHeader?.length || 0;
          })?.flat(1);
        }
      })?.flat(1);
      const longestCharacterCountForHeader = getCount?.length ? Math.max(...getCount) : 0;
      if (longestCharacterCountForHeader > 27) {
        setLongDetailHeader(true);
      } else {
        setLongDetailHeader(false);
      }
    };
    if (activeCards?.length) getCharacterCountOfHeader(activeCards);
    if (activeCardsInTab?.length) getCharacterCountOfHeader(activeCardsInTab);
  }, [activeCards, activeCardsInTab]);

  // Memoize `validatingCardData` to prevent re-creation on every render.
  const {
    validatingCardDataTop,
    validatingCardDataBottom,
    validatingCardDataListSectionBottom
  } = useMemo(() => {
    const top = activeCards ? validateContentIsPresent(activeCards, "subheader") : {};
    const bottom = activeCards ? validateContentIsPresent(activeCards, "description") : {};
    const listHeader = activeCards ? validateContentIsPresent(activeCards, "detailsHeader") : {};
    return {
      validatingCardDataTop: top,
      validatingCardDataBottom: bottom,
      validatingCardDataListSectionBottom: listHeader
    };
  }, [activeCards]);

  // CURRENCY DROPDOWN METHODS
  const onSelect = (option: currencies) => {
    setCurrencySelected(option);
  };
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  const handleDropDownSelection = (option_0: currencies) => {
    onSelect(option_0);
    setIsOpen(false);
  };

  // CLOSE DROPDOWN OUTSIDE OF CLICK
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);
  useEffect(() => {
    const renderConditionalStylesOnPills = () => {
      if (tabs?.length || activeCardsInTab?.length) {
        // useScreenSize starts at 0 until the client effect runs; skip overlap
        // offsets until we have a real viewport width.
        if (width >= 1106) {
          setStyling({
            top: "79px"
          });
        } else if (width <= 1105 && width >= 600) {
          setStyling({
            top: "179px",
            right: "0"
          });
        } else if (width > 0 && width <= 599) {
          setStyling({
            top: "0",
            right: "0"
          });
        } else {
          setStyling({});
        }
      } else {
        setStyling({});
      }
    };
    renderConditionalStylesOnPills();
  }, [width, activeCardsInTab, tabs?.length]);
  useEffect(() => {
    if (!anchorTabQuery || !containerRef.current) return;
    const matchingTab = containerRef.current.querySelector(`[data-tabanchor="${anchorTabQuery}"]`) as HTMLElement | null;
    if (matchingTab) {
      const matchingTabIndex = matchingTab.getAttribute("data-tabanchorindex") || "";
      setActiveTab(parseInt(matchingTabIndex, 10));
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  }, [anchorTabQuery]);

  // Tab list horizontal scroll affordances (mirrors InlineMediaTabView).
  const tabCount = activeCardsInTab?.length || tabs?.length || 0;
  const scrollCheck = useCallback(() => {
    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      // Free the rAF slot first so any path out of this callback leaves it
      // clean — required to keep the early-return guard from getting wedged.
      scrollRafRef.current = null;
      const el = tabListRef.current;
      if (!el) return;
      setShowRightScroll(el.scrollWidth > el.clientWidth && el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
      setShowLeftScroll(el.scrollLeft > 0);
    });
  }, []);
  const scrollTabList = (direction: "left" | "right") => {
    tabListRef.current?.scrollBy({
      left: direction === "left" ? -100 : 100,
      behavior: "smooth"
    });
  };
  useEffect(() => {
    const el_0 = tabListRef.current;
    if (!el_0) return;
    scrollCheck();
    el_0.addEventListener("scroll", scrollCheck, {
      passive: true
    });
    const ro = new ResizeObserver(() => scrollCheck());
    ro.observe(el_0);
    return () => {
      el_0.removeEventListener("scroll", scrollCheck);
      ro.disconnect();
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
        // Reset the ref too — cancelAnimationFrame prevents the rAF callback
        // (which would normally clear this) from ever running. Without this
        // reset, a later mount sees a stale non-null ref and `scrollCheck`
        // early-returns forever.
        scrollRafRef.current = null;
      }
    };
  }, [width, scrollCheck, tabCount]);
  const renderTabs = (tabs_0: tabsInterface[]) => {
    return <div className="w-full">
        <div className="t2-lo-price-cards__tablist relative z-[3] flex flex-row md:w-4/6 overflow-hidden mb-8 mt-8 max-[1105px]:mb-[132px] max-[599px]:!mb-8">
          {showLeftScroll && <button type="button" aria-label="Scroll tabs left" onClick={() => {
          scrollTabList("left");
          scrollCheck();
        }} className={`t2-tablist__scroll-button absolute left-0 top-0 bottom-0 z-[2] flex items-center justify-center cursor-pointer w-[33px] p-0 border-0 border-l-2 ${isDark ? "text-white border-white bg-[linear-gradient(90deg,_var(--color-gray-m7)_80%,_transparent)]" : "text-blue-trmb border-blue-trmb bg-[linear-gradient(90deg,_#fff_80%,_transparent)]"}`}>
              <ScrollChevron direction="left" />
            </button>}
          {showRightScroll && <button type="button" aria-label="Scroll tabs right" onClick={() => {
          scrollTabList("right");
          scrollCheck();
        }} className={`t2-tablist__scroll-button absolute right-0 top-0 bottom-0 z-[2] flex items-center justify-center cursor-pointer w-[33px] p-0 border-0 border-r-2 ${isDark ? "text-white border-white bg-[linear-gradient(270deg,_var(--color-gray-m7)_80%,_transparent)]" : "text-blue-trmb border-blue-trmb bg-[linear-gradient(270deg,_#fff_80%,_transparent)]"}`}>
              <ScrollChevron direction="right" />
            </button>}
          <div ref={tabListRef} role="tablist" className="flex items-end border-none overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {tabs_0.map((tab_0: tabsInterface, index_2: number) => {
            const tabAnchorName = tab_0?.title?.toLowerCase()?.replace(/&/g, "and")?.replace(/\s+/g, "-");
            const isActive = index_2 === activeTab;
            return <button key={`${tab_0?.title}`} type="button" role="tab" data-tab-role="tab" aria-selected={isActive ? "true" : "false"} aria-controls={`pricing-tabpanel-${index_2}`} data-tab-index={index_2} tabIndex={isActive ? 0 : -1} className={`border-b border-gray-m2 border-t-0 border-l-0 border-r-0 cursor-pointer list-none focus:outline-none focus-visible:outline-none${isActive ? ` shadow-[0px_-2px_6px_0px_rgba(0,0,0,0.08)] !p-0 bg-transparent border-b-4 ${isDark ? "border-b-white" : "border-b-blue-trmb"} t2-pricing-tab--selected` : ""}`} data-tabanchorindex={index_2} data-tabanchor={tabAnchorName} onClick={() => setActiveTab(index_2)} onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setActiveTab(index_2);
              }
            }}>
                  <h3 className={`t2-lo-price-cards__tab-title${isDark ? " t2-lo-price-cards__tab-title--dark" : ""} rounded-t-[4px] text-base leading-[1.375rem] tracking-[0.4px] px-[25px] pb-[15px] pt-2.5 font-bold uppercase m-0 h-[47px] ${isDark ? "text-gray-m6" : "text-gray-m9"}`}>
                    {tab_0?.title}
                  </h3>
                </button>;
          })}
          </div>
        </div>

      {tabs_0?.map((tab_1: tabsInterface, index_3: number) => <div key={`tp-${index_3}`} id={`pricing-tabpanel-${index_3}`} role="tabpanel" hidden={index_3 !== activeTab}>
            <div className="gap-10 flex flex-row flex-wrap justify-center">
              {tab_1?.cards?.map((card_7: card_7, cardIdx: number) => {
            return <div className="mb-5" key={`pcard-${cardIdx}`}>
                    <CardEcommerce {...card_7} id={card_7?.id} index={cardIdx} cardType={themeForCards} validatingCardDataTop={validatingCardDataTop} validatingCardDataBottom={validatingCardDataBottom} validatingCardDataListSectionBottom={validatingCardDataListSectionBottom} longDetailHeader={longDetailHeader} subscriptionSelection={selected} viewMoreLabel={viewMoreLabel} viewLessLabel={viewLessLabel} />
                  </div>;
          })}
            </div>
          </div>)}
      </div>;
  };
  return <div className={mainClasses} style={componentStyles} ref={containerRef} data-testid="layout-pricing-cards">
      <div className={wrapperClasses}>
        <div className="t2-layout__content t2-container">
          <div className="pb-8 -mx-1.5 flex flex-row flex-wrap justify-between">
            <div className="t2-header-area basis-full max-w-full min-[1080px]:basis-2/3 min-[1080px]:max-w-[66.666%] max-[1105px]:basis-full max-[1105px]:max-w-full px-2.5">
              {eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>}
              {finalHeader}
              {subheader && <div className={layoutSubheaderClasses}>{subheader}</div>}
              {description && <CommonDescription description={description} className={""} />}
            </div>
            <div className="flex items-end gap-4 relative right-[120px] max-[1105px]:right-0 max-[1105px]:top-3 max-[599px]:top-0 max-[599px]:w-full max-[599px]:mt-8 max-[599px]:flex-col max-[599px]:items-center" style={styling}>
              {toggleOptions && toggleOptions?.length && <div className={`flex rounded-[100px] border h-[52px] gap-1 w-fit z-[1] max-[599px]:w-[352px] ${isDark ? "bg-gray-m9! border-gray-m8" : "bg-gray-m2 border-[#cbcdd6]"}`}>
                  <div id="monthly" className={selected === "monthly" ? `cursor-pointer uppercase leading-7 tracking-[1.44px] font-bold text-lg self-center px-3 py-2 h-11 max-[599px]:w-full max-[599px]:text-center ${isDark ? "text-gray-m6" : "text-gray-m5"} ${isDark ? "mx-1 relative border border-white rounded-[100px] bg-gray-m7 text-white" : "mx-1 relative border border-gray-m10 rounded-[100px] bg-white text-gray-m10"}` : `cursor-pointer uppercase leading-7 tracking-[1.44px] font-bold text-lg self-center px-3 py-2 h-11 max-[599px]:w-full max-[599px]:text-center ${isDark ? "text-gray-m6" : "text-gray-m5"}`} onClick={() => setSelected("monthly")}>
                    {toggleOptions?.[0] || "Monthly"}
                  </div>
                  <div id="annual" className={selected === "annual" ? `cursor-pointer uppercase leading-7 tracking-[1.44px] font-bold text-lg self-center px-3 py-2 h-11 max-[599px]:w-full max-[599px]:text-center ${isDark ? "text-gray-m6" : "text-gray-m5"} ${isDark ? "mx-1 relative border border-white rounded-[100px] bg-gray-m7 text-white" : "mx-1 relative border border-gray-m10 rounded-[100px] bg-white text-gray-m10"}` : `cursor-pointer uppercase leading-7 tracking-[1.44px] font-bold text-lg self-center px-3 py-2 h-11 max-[599px]:w-full max-[599px]:text-center ${isDark ? "text-gray-m6" : "text-gray-m5"}`} onClick={() => setSelected("annual")}>
                    {toggleOptions?.[1] || "Annual"}
                  </div>
                </div>}
              {currencies && currencies?.length > 0 && <div className="t2-lo-price-card__currencies-dropdown relative cursor-pointer h-[52px] mr-0.5" ref={dropdownRef}>
                  <div className={`t2-lo-price-card__currencies-dropdown-button absolute h-[52px] z-[1] flex text-base border border-gray-m10 w-[116px] rounded-[100px] px-4 py-3.5 justify-center font-bold max-[599px]:w-[352px] max-[599px]:relative max-[599px]:justify-between ${isDark ? "bg-gray-m7 border-white text-white" : "bg-white"} [&_.t2-icon]:mt-1.5 [&_.t2-icon]:ml-3`} onClick={toggleDropdown}>
                    {currencySelected?.displayName ? currencySelected.displayName : currencies?.[0]?.displayName}
                    <Icon type={"t2u-chevron-right"} rotateDeg="90" size={12} className="t2-dropdown__icon" />
                  </div>

                  {isOpen && <div className="flex flex-col text-base border border-gray-m10 justify-between absolute bg-white w-[116px] z-[1] max-[599px]:w-[352px] max-[599px]:top-0 rounded-[20px]">
                      {currencies?.length && currencies?.map(option_1 => <div key={`currency-${option_1?.machineName}`} className={`t2-lo-price-card__currencies-dropdown-item border-b border-gray-m2 font-bold px-[22px] py-3 block cursor-pointer last:rounded-b-[20px] first:rounded-t-[20px] hover:bg-gray-m2 first:hover:bg-gray-m2 ${isDark ? "bg-gray-m7 text-white border-white hover:bg-gray-m9 first:hover:bg-gray-m9" : "text-gray-m9"}`} onClick={() => handleDropDownSelection(option_1)}>
                            {option_1?.displayName}
                          </div>)}
                    </div>}
                </div>}
            </div>
            {activeCardsInTab?.length ? renderTabs(activeCardsInTab) : tabs?.length ? renderTabs(tabs) : ""}
          </div>
          {activeCards?.length && activeCards?.length > 0 ? <div className="gap-10 flex flex-row flex-wrap justify-center">
              {activeCards?.map((card_8, index_4) => <div className="mb-5" key={index_4}>
                  <CardEcommerce {...card_8} index={index_4} id={card_8?.id} cardType={themeForCards} validatingCardDataTop={validatingCardDataTop} validatingCardDataBottom={validatingCardDataBottom} validatingCardDataListSectionBottom={validatingCardDataListSectionBottom} longDetailHeader={longDetailHeader} subscriptionSelection={selected} viewMoreLabel={viewMoreLabel} viewLessLabel={viewLessLabel} />
                </div>)}
            </div> : <div className="gap-10 flex flex-row flex-wrap justify-center"></div>}
          {buttons && buttons.length > 0 && <div className="t2-layout__cta flex flex-wrap gap-y-7 gap-x-4 md:gap-y-4 max-sm:flex-col justify-center pt-[50px]">
              {buttons.map((btn, index_5) => <TerraLink key={`button-${index_5}`} {...btn} />)}
            </div>}
          {additionalPricingInformation && <div className="flex flex-row flex-wrap justify-center pt-8">
              <div className="t2-lo-price-cards__additional-pricing-information">
                <CommonDescription description={additionalPricingInformation} className={"w-full"} />
              </div>
            </div>}
          <section>
            {excludesTaxLabel && <p className="flex justify-center text-gray-m7 text-base leading-[1.375rem] font-semibold mt-5 mx-0 mb-0 max-[599px]:text-center !mt-20">
                {excludesTaxLabel}
              </p>}
            {displayCardPaymentOptionsMedia && renderCreditCardPaymentAssets()}
            {paymentText && <p className="flex justify-center text-gray-m7 text-base leading-[1.375rem] font-semibold mt-5 mx-0 mb-0 max-[599px]:text-center">
                {paymentText}
              </p>}
            {comparePlansText && <div className="mt-20 flex justify-center items-center h-5">
                <p className={`${layoutEyebrowClasses} mb-0 mr-2`}>
                  {comparePlansText}
                </p>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M14.99 12H18.79C19.24 12 19.46 12.54 19.14 12.85L12.7 19.29C12.6075 19.3827 12.4976 19.4563 12.3766 19.5064C12.2557 19.5566 12.126 19.5824 11.995 19.5824C11.864 19.5824 11.7344 19.5566 11.6134 19.5064C11.4924 19.4563 11.3825 19.3827 11.29 19.29L4.85001 12.85C4.78149 12.7801 4.73498 12.6917 4.71626 12.5956C4.69754 12.4995 4.70743 12.4001 4.74469 12.3096C4.78196 12.2191 4.84496 12.1415 4.9259 12.0865C5.00684 12.0314 5.10215 12.0014 5.20001 12H8.99001V5C8.99001 4.45 9.44001 4 9.99001 4H13.99C14.54 4 14.99 4.45 14.99 5V12Z" fill={isDark ? "#fbad26" : "#0063A3"} />
                </svg>
              </div>}
          </section>
        </div>
      </div>
    </div>;
};